import React, { useState, useMemo, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Shield, Lock } from 'lucide-react';
import LegalModal from '../legal/LegalModal.jsx';

const EXTRA_SERVICE_LABELS = {
  oven: 'Oven Cleaning',
  fridge: 'Fridge Cleaning',
  baseboards: 'Baseboard Detailing',
  petHair: 'Pet Hair Removal',
  dishwashing: 'Dishwashing by Hand',
  windows: 'Interior Windows',
  cabinets: 'Cabinet & Drawer Interiors',
  laundry: 'Laundry Service'
};

const SERVICE_LABELS = {
  basic: 'Basic Cleaning',
  deep: 'Deep Cleaning'
};

const API_URL = process.env.REACT_APP_API_URL;
export default function Payment({
  total,
  entryInfo,
  rooms,
  baths,
  size,
  selectedExtras = [],
  selectedService,
  validZip,
  customerInfoComplete = false,
  requests = '',
  calBooked = false,
  onMissingRequirements,
  onRequirementFocus,
  highlightTerms = false,
  bookingId = null,
  customerId = null,
  customerEmail = '',
  calBookingUid = null,
  customerDetails = {}
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [slotTaken, setSlotTaken] = useState(false);
  const [hasAgreed, setHasAgreed] = useState(false);
  const [hasShownMissing, setHasShownMissing] = useState(false);
  const [showMissingHint, setShowMissingHint] = useState(false);
  const missingHintRef = useRef(null);
  const prevHintVisible = useRef(false);
  const skipHintScrollRef = useRef(false);
  const extraRowRef = useRef(null);
  const extraLabelMeasureRef = useRef(null);
  const extraValueMeasureRef = useRef(null);
  const [consentResetKey, setConsentResetKey] = useState(0); // forces checkbox re-render when locked
  const [activeLegalDoc, setActiveLegalDoc] = useState(null);
  const [extraInline, setExtraInline] = useState(true);

  const getStripe = async () => {
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) {
      throw new Error('Stripe publishable key missing. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.');
    }
    return loadStripe(publishableKey);
  };

  const extrasLabel = useMemo(() => {
    if (!selectedExtras.length) return 'None';
    return selectedExtras
      .map((id) => EXTRA_SERVICE_LABELS[id] || id)
      .join(', ');
  }, [selectedExtras]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Measure the extra-services line to decide inline vs stacked layout.
    const measureExtraInline = () => {
      const row = extraRowRef.current;
      const labelMeasure = extraLabelMeasureRef.current;
      const valueMeasure = extraValueMeasureRef.current;
      if (!row || !labelMeasure || !valueMeasure) return;

      const rowWidth = row.clientWidth;
      const labelWidth = labelMeasure.offsetWidth;
      const valueWidth = valueMeasure.offsetWidth;
      const horizontalGap = 16;
      const available = Math.max(rowWidth - labelWidth - horizontalGap, 0);
      const fits = valueWidth <= available;
      setExtraInline((prev) => (prev === fits ? prev : fits));
    };

    let rafId = requestAnimationFrame(measureExtraInline);
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measureExtraInline);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [extrasLabel]);

  const isEntryInfoValid = () => {
    if (!entryInfo.type) return false;
    if ((entryInfo.type === 'hiding_spot' || entryInfo.type === 'lockbox') && !entryInfo.notes) {
      return false;
    }
    return true;
  };

  const handleLegalModal = (event, docType) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveLegalDoc(docType);
  };

  const isCalBooked = Boolean(calBooked && bookingId && calBookingUid);
  const entryReady = isEntryInfoValid();
  const consentLocked = !isCalBooked;

  const missingSections = useMemo(() => {
    const sections = [];
    if (!customerInfoComplete) sections.push('customer');
    if (!entryReady) sections.push('entry');
    if (!isCalBooked) sections.push('cal');
    if (!hasAgreed) sections.push('terms');
    return sections;
  }, [customerInfoComplete, entryReady, isCalBooked, hasAgreed]);

  const requirementLabels = {
    customer: 'Add your contact & address details',
    entry: 'Add entry details or instructions',
    cal: 'Finish scheduling through calendar',
    terms: (
      <>
        Agree to the Terms of Service &{' '}
        <span className="payment-legal-nowrap">Privacy Policy</span>
      </>
    )
  };

  const triggerRequirementHints = (customSections) => {
    const sections = customSections || missingSections;
    if (!sections.length) return false;
    setHasShownMissing(true);
    setShowMissingHint(true);
    onMissingRequirements?.(sections);
    return true;
  };

  // ✅ Fixed useEffect – stabilized dependencies
  useEffect(() => {
    if (!hasShownMissing) return;

    if (!missingSections.length) {
      setHasShownMissing(false);
      setShowMissingHint(false);
      onMissingRequirements?.([]);
    } else {
      // Only show the hint once until requirements change
      setShowMissingHint(true);
      onMissingRequirements?.(missingSections);
    }
  }, [hasShownMissing, entryReady, isCalBooked, hasAgreed, onMissingRequirements, missingSections]);

  // ✅ Cleanup on unmount — no dependency to avoid repeated unmount/mount loops
  useEffect(() => {
    return () => {
      onMissingRequirements?.([]);
    };
  }, [onMissingRequirements]); // intentionally empty

  useEffect(() => {
    if (consentLocked && hasAgreed) {
      setHasAgreed(false);
      setConsentResetKey((prev) => prev + 1);
    }
  }, [consentLocked, hasAgreed]);

  const scrollToHint = (targetY, duration = 1200) => {
    if (typeof window === 'undefined') return undefined;
    const start = window.scrollY;
    const distance = targetY - start;
    if (distance === 0) return undefined;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let animationFrame;
    let startTime = null;

    const step = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(progress);
      window.scrollTo(0, start + distance * eased);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };

    animationFrame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(animationFrame);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let cancelAnimation;
    if (showMissingHint && !prevHintVisible.current && missingHintRef.current) {
      if (skipHintScrollRef.current) {
        skipHintScrollRef.current = false;
        prevHintVisible.current = showMissingHint;
        return undefined;
      }
      const rect = missingHintRef.current.getBoundingClientRect();
      const targetY = Math.max(rect.top + window.scrollY - 140, 0);
      cancelAnimation = scrollToHint(targetY, 1400);
    }
    prevHintVisible.current = showMissingHint;
    return () => {
      if (typeof cancelAnimation === 'function') cancelAnimation();
    };
  }, [showMissingHint]);

  const handleRequirementClick = (section) => {
    skipHintScrollRef.current = true;
    triggerRequirementHints([section]);
    onRequirementFocus?.(section);
  };

  const normalizedCustomerDetails =
    customerDetails && typeof customerDetails === 'object' ? customerDetails : {};
  const customerName = [normalizedCustomerDetails.firstName, normalizedCustomerDetails.lastName]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
    .join(' ');

  const handlePayment = async () => {
    if (missingSections.length) {
      triggerRequirementHints();
      setCheckoutError('Complete the highlighted sections to continue.');
      return;
    }

    if (!validZip) {
      setCheckoutError('Enter a valid service ZIP to continue.');
      return;
    }

    if (!bookingId) {
      triggerRequirementHints(['cal']);
      setCheckoutError('We could not find your booking. Please reopen the calendar and finish scheduling.');
      return;
    }

    setCheckoutError('');
    setSlotTaken(false);
    setIsProcessing(true);

    try {
      const stripe = await getStripe();
      if (!stripe) throw new Error('Unable to initialize Stripe. Please try again later.');

      const response = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceKey: selectedService,
          size,
          rooms,
          baths,
          extras: selectedExtras,
          entryInfo,
          requests,
          bookingId,
          customerId,
          customerEmail,
          calBookingUid,
          customerDetails: normalizedCustomerDetails,
          consentAccepted: hasAgreed,
          totalOverride: total,
          customer: {
            email: customerEmail || undefined,
            name: customerName || undefined
          }
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (response.status === 409 && payload?.code === 'slot_taken') {
          setSlotTaken(true);
          setCheckoutError(
            payload?.error ||
              'That time is no longer available. Click to pick another time.'
          );
          setIsProcessing(false);
          return;
        }
        throw new Error(
          payload?.error || 'Unable to start checkout. Please review your details and try again.'
        );
      }

      const session = await response.json();
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) throw new Error(result.error.message);
    } catch (error) {
      setCheckoutError(error.message || 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div
          className="feature-badge gold mx-auto"
          style={{ background: 'linear-gradient(135deg, var(--gold-500), var(--gold-300))' }}
        >
          <CreditCard className="w-8 h-8 u-ink-800" />
        </div>
        <h2
          className="section-heading"
          style={{ textAlign: 'center', marginTop: '16px', marginBottom: '16px' }}
        >
          Complete Your Booking
        </h2>
      </div>

      <div className="payment-card">
        {/* --- Order Summary --- */}
        <div className="payment-card__section">
          <h4 className="payment-card__heading">Order Summary</h4>
          <div className="payment-summary">
            <div className="payment-summary__row">
              <span className="payment-summary__label">Service Type:</span>
              <span className="payment-summary__value payment-summary__value--nowrap">
                {SERVICE_LABELS[selectedService] || selectedService}
              </span>
            </div>
            <div className="payment-divider" />
            <div className="payment-summary__row">
              <span className="payment-summary__label">Square Feet:</span>
              <span className="payment-summary__value">{size.toLocaleString()} sq ft</span>
            </div>
            <div className="payment-divider" />
            <div className="payment-summary__row">
              <span className="payment-summary__label">Rooms:</span>
              <span className="payment-summary__value">{rooms === 'Studio' ? 'Studio' : rooms}</span>
            </div>
            <div className="payment-divider" />
            <div className="payment-summary__row">
              <span className="payment-summary__label">Bathrooms:</span>
              <span className="payment-summary__value">{baths}</span>
            </div>
            <div className="payment-divider" />
            <div
              className={
                'payment-summary__row' + (extraInline ? '' : ' payment-summary__row--stacked')
              }
              ref={extraRowRef}
            >
              <span className="payment-summary__label">Extra Services:</span>
              <span
                className={
                  'payment-summary__value' +
                  (extraInline
                    ? ' payment-summary__value--nowrap'
                    : ' payment-summary__value--multiline')
                }
              >
                {extrasLabel}
              </span>
              <span
                aria-hidden="true"
                className="payment-summary__label payment-summary__measure"
                ref={extraLabelMeasureRef}
              >
                Extra Services:
              </span>
              <span
                aria-hidden="true"
                className="payment-summary__value payment-summary__value--nowrap payment-summary__measure"
                ref={extraValueMeasureRef}
              >
                {extrasLabel}
              </span>
            </div>
            <div className="payment-divider" />
          </div>
        </div>

        {/* --- Total --- */}
        <div className="payment-card__section payment-total">
          <span className="payment-total__label">Total:</span>
          <span className="payment-total__value">${total.toFixed(2)}</span>
        </div>

        <div className="payment-divider" />

        {!validZip && <p className="payment-note">Enter a valid service ZIP to enable checkout.</p>}

        {/* --- Terms Checkbox --- */}
        <div
          className={
            'payment-legal-consent' +
            (highlightTerms ? ' highlight-required-legal' : '') +
            (consentLocked ? ' payment-legal-consent--locked' : '')
          }
          aria-live={consentLocked ? 'polite' : undefined}
        >
          <label className="payment-legal-checkbox" htmlFor="legal-consent-checkbox">
            <input
              key={consentResetKey}
              id="legal-consent-checkbox"
              type="checkbox"
              checked={hasAgreed}
              disabled={consentLocked}
              onChange={(event) => setHasAgreed(event.target.checked)}
            />
            <span className="payment-legal-text">
              I agree to the{' '}
              <button
                type="button"
                className="payment-legal-link"
                onClick={(event) => handleLegalModal(event, 'terms')}
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <span className="payment-legal-nowrap">
                <button
                  type="button"
                  className="payment-legal-link"
                  onClick={(event) => handleLegalModal(event, 'privacy')}
                >
                  Privacy Policy
                </button>
                .
              </span>
            </span>
          </label>
          {consentLocked && (
            <div className="calendar-guard payment-consent-guard">
              <p className="calendar-guard__title">Book your spot before consenting:</p>
              <ul className="calendar-guard__list">
                <li>
                  <button
                    type="button"
                    className="calendar-guard__link"
                    onClick={() => handleRequirementClick('cal')}
                  >
                    Open calendar and finish scheduling
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>

        {/* --- Payment Button --- */}
        <div
          className="payment-button-guard"
          onMouseEnter={() => (!validZip || missingSections.length ? triggerRequirementHints() : null)}
          onFocusCapture={() => (!validZip || missingSections.length ? triggerRequirementHints() : null)}
          onTouchStart={() => (!validZip || missingSections.length ? triggerRequirementHints() : null)}
        >
          <button
            type="button"
            onClick={handlePayment}
            disabled={!validZip || missingSections.length > 0 || isProcessing}
            className="payment-button"
          >
            {isProcessing ? (
              <>
                <div className="payment-spinner" />
                Redirecting...
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                Book & Pay - ${total.toFixed(2)}
              </>
            )}
          </button>
        </div>

        {/* --- Missing Requirements Hint --- */}
        {showMissingHint && missingSections.length > 0 && (
          <div className="payment-missing-hint" role="status" ref={missingHintRef}>
            <p>
              {missingSections.length === 1
                ? 'Almost there—finish this step:'
                : 'Almost there - complete these steps:'}
            </p>
            <ul className={missingSections.length === 1 ? 'single-step' : ''}>
              {missingSections.map((section) => (
                <li key={section}>
                  <button
                    type="button"
                    className="payment-missing-link"
                    onClick={() => handleRequirementClick(section)}
                  >
                    {requirementLabels[section]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* --- Error Message --- */}
        {slotTaken ? (
          <div className="payment-error" role="alert">
            <p>{checkoutError || 'That time is no longer available.'}</p>
            <button
              type="button"
              className="payment-missing-link"
              onClick={() => handleRequirementClick('cal')}
            >
              Click to pick another time
            </button>
          </div>
        ) : (
          checkoutError && (
            <p className="payment-error" role="alert">
              {checkoutError}
            </p>
          )
        )}

        {/* --- Security Icons --- */}
        <div className="payment-security">
          <div className="payment-security__item">
            <Lock className="w-5 h-5" />
            <span>SSL Encrypted</span>
          </div>
          <div className="payment-security__item">
            <Shield className="w-5 h-5" />
            <span>Secure Payment</span>
          </div>
        </div>
      </div>

      {activeLegalDoc && (
        <LegalModal
          docType={activeLegalDoc}
          title={
            activeLegalDoc === 'terms'
              ? 'Terms of Service'
              : 'Privacy Policy'
          }
          onClose={() => setActiveLegalDoc(null)}
        />
      )}
    </div>
  );
}
